import { useCallback, useMemo, useRef, useState } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { MessagingInboxUser } from '@wetags/messaging-inbox';
import { useQueryClient } from 'react-query';
import { DraftV1 } from '@frontend/api-sms-draft';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { useAlert } from '@frontend/design-system';

// Prefix used to purge all cached variants of the smart draft query when a draft is dismissed.
const SMART_DRAFT_QUERY_KEY_PREFIX = ['DraftV1', 'GenerateSmartDraft'];

type UseSmartDraftProps = {
  smartDraftMessages: SMS[];
  threadId?: string;
  aiBotStatusActive?: boolean;
};

export function useSmartDraft({ smartDraftMessages, threadId, aiBotStatusActive }: UseSmartDraftProps) {
  // Tracks which thread the user dismissed (insert or X).
  const [dismissedForThreadId, setDismissedForThreadId] = useState<string | null>(null);
  // Tracks the previous thread so we can reset dismissal when the thread changes.
  const [prevResolvedThreadId, setPrevResolvedThreadId] = useState<string | undefined>(undefined);
  // Tracks whether the user explicitly requested generation (for manual-create mode).
  const [userRequestedGenerate, setUserRequestedGenerate] = useState(false);
  const stableMessagesRef = useRef<{ ids: string; messages: SMS[] }>({ ids: '', messages: [] });
  const queryClient = useQueryClient();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const alert = useAlert();
  const { t } = useTranslation();

  const hasSmartDraftsEnabled = getFeatureFlagValue('inbox-smart-drafts');
  // When wetag is enabled, auto-generation is disabled; drafts are only generated on-demand.
  // We also check the loading state to prevent auto-generation while wetag is loading.
  const [isManualCreateMode, isWetagLoading] = MessagingInboxUser.useWetag('smart-draft-manual-create');
  const resolvedThreadId = threadId || smartDraftMessages[0]?.threadId;

  // Render-phase reset: when the thread changes, clear the dismissal and user request
  // so returning to a previously-dismissed thread starts fresh (no useEffect needed).
  if (prevResolvedThreadId !== resolvedThreadId) {
    setPrevResolvedThreadId(resolvedThreadId);
    setDismissedForThreadId(null);
    setUserRequestedGenerate(false);
  }

  // dismissed is true only while actively on the thread where the user dismissed.
  const dismissed = dismissedForThreadId === resolvedThreadId && dismissedForThreadId !== null;

  const { stableMessages, serializedMessageData, hasInbound } = useMemo(() => {
    if (!smartDraftMessages?.length) {
      return { stableMessages: [], serializedMessageData: '', hasInbound: false };
    }

    const currentIds = smartDraftMessages
      .map((msg) => `${msg.id}-${msg.locationId}`)
      .sort()
      .join('|');
    const { ids: previousIds, messages: previousMessages } = stableMessagesRef.current;

    let finalMessages: SMS[];
    if (currentIds !== previousIds) {
      stableMessagesRef.current = { ids: currentIds, messages: smartDraftMessages };
      finalMessages = smartDraftMessages;
    } else {
      finalMessages = previousMessages.length ? previousMessages : smartDraftMessages;
    }

    return {
      stableMessages: finalMessages,
      serializedMessageData: currentIds,
      hasInbound: finalMessages.some((sms) => sms.direction === Direction.DIRECTION_INBOUND),
    };
  }, [smartDraftMessages]);

  const shouldShowSmartDraft = hasSmartDraftsEnabled && !!serializedMessageData && !aiBotStatusActive && hasInbound;
  const genericSmartDraftErrorMessage = t('Error generating smart draft');
  const rateLimitedSmartDraftErrorMessage = t('Smart Reply generation failed. Try again.');

  // In manual-create mode (wetag ON), only generate when user explicitly clicks the button.
  // In auto mode (wetag OFF), generate automatically on thread open (legacy behavior).
  // While wetag is loading, don't auto-generate to prevent unnecessary API calls.
  const shouldAutoGenerate = !isWetagLoading && (!isManualCreateMode || userRequestedGenerate);

  const { data, isFetching, refetch } = DraftV1.Queries.useGenerateSmartDraftQuery({
    request: { messages: stableMessages },
    options: {
      // In manual-create mode: waits for user to click "Create smart reply" button.
      // In auto mode: starts on thread open. Disabled when dismissed in this session.
      // Clearing the RQ cache (removeQueries) on dismiss ensures a fresh fetch on next enable.
      enabled: shouldShowSmartDraft && !dismissed && shouldAutoGenerate,
      onSuccess: ({ suggestedMessage }) => {
        if (!suggestedMessage) {
          alert.error(genericSmartDraftErrorMessage);
        }
      },
      onError: (error: any) => {
        const isRateLimited = error?.status === 429;

        alert.error(isRateLimited ? rateLimitedSmartDraftErrorMessage : genericSmartDraftErrorMessage);

        if (isRateLimited) {
          return;
        }

        sentry.error({
          error,
          topic: 'messages',
          team: 'fe-messaging',
          severityLevel: 'error',
        });
      },
    },
  });

  // Purges React Query's internal cache for this query so the next enable triggers a fresh fetch
  // (not a stale-data cache hit). Called on insert, close, and redo.
  const clearQueryCache = useCallback(() => {
    queryClient.removeQueries(SMART_DRAFT_QUERY_KEY_PREFIX);
  }, [queryClient]);

  // Insert accepted: text inserted externally by caller, dismiss and wipe cache so reopen
  // always generates a fresh draft rather than restoring the accepted text.
  // Reset userRequestedGenerate so manual-create mode requires another click.
  const acceptSmartDraft = useCallback(() => {
    setUserRequestedGenerate(false);
    setDismissedForThreadId(resolvedThreadId ?? null);
    clearQueryCache();
  }, [resolvedThreadId, clearQueryCache]);

  // X / close: discard preview, dismiss and wipe cache so reopen generates fresh.
  // Reset userRequestedGenerate so manual-create mode requires another click.
  const closeSmartDraft = useCallback(() => {
    setUserRequestedGenerate(false);
    setDismissedForThreadId(resolvedThreadId ?? null);
    clearQueryCache();
  }, [resolvedThreadId, clearQueryCache]);

  // refetch() forces a network request even if RQ somehow retained stale data.
  // In manual-create mode, this is the primary trigger for generation.
  const generateSmartDraft = useCallback(() => {
    setUserRequestedGenerate(true);
    setDismissedForThreadId(null);
    refetch();
  }, [refetch]);

  // Redo: wipe cache and force a fresh fetch without changing dismissed state.
  const redoSmartDraft = useCallback(() => {
    clearQueryCache();
    refetch();
  }, [clearQueryCache, refetch]);

  const smartDraft = data?.suggestedMessage ?? '';

  // In manual-create mode: show button when eligible and user hasn't requested generation yet,
  // or after dismissal. In auto mode: show button when dismissed or idle (no result, not loading).
  const isIdleInManualMode = isManualCreateMode && !userRequestedGenerate;
  const showSmartDraftButton =
    shouldShowSmartDraft && (dismissed || isIdleInManualMode || (!isFetching && !smartDraft));
  // Loading state: only show when generation is in progress (query enabled and fetching).
  const isLoadingSmartDraft = shouldShowSmartDraft && !dismissed && shouldAutoGenerate && isFetching;
  const showSmartDraftResult = shouldShowSmartDraft && !dismissed && !!smartDraft && !isFetching;

  return {
    smartDraft,
    showSmartDraftButton,
    // Button should be borderless only after explicit dismissal, not in initial manual-create idle state.
    showSmartDraftButtonNoBorder: dismissed,
    isLoadingSmartDraft,
    showSmartDraftResult,
    generateSmartDraft,
    redoSmartDraft,
    acceptSmartDraft,
    closeSmartDraft,
  };
}
